import { cameraPoseChanged, clonePreservingType, DetectionZone, DetectionZoneType, Device, Facilities, LaneLine, reprojectShapePoints, ScreenLine, selectAnchoredShapes, Vector2 } from "@app/shared";
import { useCallback, useState } from "react";
import { divideQuadrilateral, toastWarning } from "../../../../helpers";
import { MapShapeTypes } from "../../maps";
import { EditTool, EditToolType, findToolByShape } from "../EditTool";
import { shapeToName } from "../ShapeConfiguration";
import { ShapeManagerState } from "./useShapeManager";
import { ToolManagerState } from "./useToolManager";

interface UseShapeLifecycleProps {
    facilityId: string | undefined;
    shapeManager: ShapeManagerState;
    toolManager: ToolManagerState;
}

export interface ShapeLifecycleState {
    copiedShapes: (DetectionZone | ScreenLine | LaneLine)[];

    handleShapeChanged: (shape: MapShapeTypes, shouldDelete: boolean, wasCompletedByMiddleClick?: boolean) => Promise<void>;
    copyShapes: (shapes?: (DetectionZone | ScreenLine | LaneLine)[]) => void;
    pasteShapes: (shapes?: (DetectionZone | ScreenLine | LaneLine)[], targetPosition?: Vector2) => void;
    splitSpot: (shape: MapShapeTypes, spotCount: number) => void;
    cloneShape: (shape: ScreenLine | DetectionZone | LaneLine) => ScreenLine | DetectionZone | LaneLine;
    clearCopiedShapes: () => void;

    canDeleteShape: (shapeId: string) => Promise<boolean>;
}

// Helper function to generate proper copy names based on existing shapes
function generateCopyName(originalName: string | undefined, existingShapes: (DetectionZone | ScreenLine | LaneLine)[]): string | undefined {
    if (!originalName) return undefined;

    // Extract the base name (remove any existing copy suffix)
    const copyRegex = /^(.+?)\s*\(Copy(?:\s+(\d+))?\)$/;
    const match = originalName.match(copyRegex);
    const baseName = match ? match[1].trim() : originalName;

    // Find all existing copy numbers for this base name
    const existingCopyNumbers = new Set<number>();
    existingShapes.forEach((shape) => {
        if (shape.name) {
            // Check if this shape has the same base name
            if (shape.name === baseName) {
                // Original name exists, so we'll need at least (Copy)
                existingCopyNumbers.add(0); // 0 represents the original "(Copy)" without number
            } else if (shape.name.startsWith(baseName)) {
                const copyMatch = shape.name.match(new RegExp(`^${baseName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*\\(Copy(?:\\s+(\\d+))?\\)$`));
                if (copyMatch) {
                    const copyNum = copyMatch[1] ? parseInt(copyMatch[1]) : 1;
                    existingCopyNumbers.add(copyNum);
                }
            }
        }
    });

    // Find the next available number
    let nextNumber = 1;
    while (existingCopyNumbers.has(nextNumber)) {
        nextNumber++;
    }

    // Generate the appropriate name
    if (nextNumber === 1) {
        return `${baseName} (Copy)`;
    } else {
        return `${baseName} (Copy ${nextNumber})`;
    }
}

// Helper function to calculate the center point of a group of shapes
function calculateShapesCenter(shapes: (DetectionZone | ScreenLine | LaneLine)[]): Vector2 | null {
    if (shapes.length === 0) return null;

    let totalX = 0;
    let totalY = 0;
    let pointCount = 0;

    shapes.forEach((shape) => {
        shape.points?.forEach((point) => {
            totalX += point.x;
            totalY += point.y;
            pointCount++;
        });
    });

    if (pointCount === 0) return null;

    return new Vector2({ x: totalX / pointCount, y: totalY / pointCount });
}

export function useShapeLifecycle({ facilityId, shapeManager, toolManager }: UseShapeLifecycleProps): ShapeLifecycleState {
    const [copiedShapes, setCopiedShapes] = useState<(DetectionZone | ScreenLine | LaneLine)[]>([]);

    const handleShapeChanged = useCallback(
        async (shape: MapShapeTypes, shouldDelete: boolean, wasCompletedByMiddleClick?: boolean): Promise<void> => {
            let canDelete = true;
            let deleteBlockReason = "";

            if (shouldDelete && shape.id) {
                const isTempId = /^\d+$/.test(shape.id);

                if (!isTempId && facilityId) {
                    try {
                        const result = await Facilities.checkIfCanDeleteShape(facilityId, shape.id);
                        canDelete = result?.canDelete ?? false;
                        if (!canDelete) {
                            deleteBlockReason = result?.reason || "This shape cannot be deleted because it has active event triggers associated with it.";
                        }
                    } catch (error) {
                        console.error("Error checking if shape can be deleted:", error);
                        canDelete = false;
                        deleteBlockReason = "Unable to verify if shape can be deleted. Please try again.";
                    }
                }
            }

            if (shouldDelete && canDelete) {
                shapeManager.deleteShape(shape.id!);
                return;
            }

            if (shouldDelete && !canDelete) {
                const shapeName = shape.name || shapeToName(shape);
                toastWarning(`Cannot Delete ${shapeName}`, deleteBlockReason, 8000);
                return;
            }

            if (!shouldDelete) {
                // A camera-drawn shape marks a feature in the photo. Correcting the device's height
                // or tilt doesn't move that feature, so the shape has to keep covering the same
                // pixels — which means re-deriving its ground points under the new pose. Skipping
                // this leaves coordinates frozen at values computed from the old pose: a ~1.5 m
                // height correction is ~30 m of ground error at 50 m range, and barely visible on
                // screen. Position/heading are excluded here; the alignment rig already carries those.
                if (shape.$type === Device.$type) {
                    const nextDevice = shape as Device;
                    const previousDevice = shapeManager.allShapes.find((s) => s.id === nextDevice.id && s.$type === Device.$type) as Device | undefined;
                    if (previousDevice && cameraPoseChanged(previousDevice, nextDevice)) {
                        const isOnlyDevice = shapeManager.allShapes.filter((s) => s.$type === Device.$type).length <= 1;
                        const stale: string[] = [];
                        selectAnchoredShapes(shapeManager.allShapes, nextDevice, isOnlyDevice).forEach((anchored) => {
                            const points = reprojectShapePoints(previousDevice, nextDevice, anchored.points ?? []);
                            if (points) shapeManager.updateShape(clonePreservingType(anchored, { points }) as any);
                            else stale.push(anchored.name || shapeToName(anchored as MapShapeTypes));
                        });
                        if (stale.length > 0) {
                            toastWarning("Some shapes could not be re-aligned", `${stale.join(", ")} ${stale.length === 1 ? "has a point" : "have points"} at or past the horizon, so they were left as drawn. Check them against the camera view.`, 10000);
                        }
                    }
                }

                shapeManager.updateShape(shape as any);
                const hasIsComplete = typeof (shape as any).isComplete === "function";
                if (hasIsComplete && shape.isComplete() && toolManager.currentTool) {
                    // if it's a parking spot that was just completed, potentially split it
                    if (shape.$type === DetectionZone.$type && toolManager.currentTool?.toolType === EditToolType.ParkingSpot) {
                        // this would trigger the split dialog in the UI
                        // the actual split logic is handled by splitSpot method
                    }

                    if (toolManager.canContinueWithTool()) {
                        toolManager.createShapeWithTool(toolManager.currentTool);
                    } else {
                        toolManager.clearTool();
                    }
                }
            }
        },
        [facilityId, shapeManager, toolManager]
    );

    const splitSpot = useCallback(
        (shape: MapShapeTypes, spotCount: number) => {
            if (shape.$type !== DetectionZone.$type) return;

            const detectionZone = shape as DetectionZone;
            const shapePoints = [...(detectionZone.points || [])];
            if (shapePoints.length > 0) {
                shapePoints.pop(); // Remove the duplicate last point
            }

            const spotPoints = divideQuadrilateral(shapePoints, spotCount);
            const newSpots = [...Array(spotCount)].map((_, idx) => {
                const spot = EditTool.ParkingSpot.createShape(toolManager.getNextTempId()) as DetectionZone;
                spot.zoneType = DetectionZoneType.ParkingSpot;
                spot.points = spotPoints[idx].map((pt) => new Vector2({ x: pt.x, y: pt.y }));
                spot.points.push(spot.points[0]); // Close the shape
                return spot;
            });

            // Remove the original shape and add the new spots
            shapeManager.deleteShape(shape.id!);
            newSpots.forEach((spot) => shapeManager.addShape(spot));
            shapeManager.selectShapes(newSpots as MapShapeTypes[]);
        },
        [shapeManager, toolManager]
    );

    const cloneShape = useCallback(
        (shape: ScreenLine | DetectionZone | LaneLine): ScreenLine | DetectionZone | LaneLine => {
            const tool = findToolByShape(shape);
            if (!tool) {
                throw new Error(`No tool found for shape type: ${shape.$type}`);
            }

            const clone = tool.createShape(toolManager.getNextTempId()) as ScreenLine | DetectionZone | LaneLine;
            clone.points = shape.points?.map((point) => new Vector2({ x: point.x, y: point.y })) ?? [];
            clone.facilityId = shape.facilityId;
            clone.name = generateCopyName(shape.name, shapeManager.allShapes as (DetectionZone | ScreenLine | LaneLine)[]);

            // handle specific shape properties
            if (shape.$type === ScreenLine.$type) {
                const screenLine = shape as ScreenLine;
                (clone as ScreenLine).inboundDirection = screenLine.inboundDirection;
                (clone as ScreenLine).outboundDirection = screenLine.outboundDirection;
            } else if (shape.$type === DetectionZone.$type) {
                const detectionZone = shape as DetectionZone;
                (clone as DetectionZone).zoneType = detectionZone.zoneType;
                (clone as DetectionZone).zoneCardinalDirection = detectionZone.zoneCardinalDirection;
                (clone as DetectionZone).zoneIndex = detectionZone.zoneIndex;
            } else if (shape.$type === LaneLine.$type) {
                const laneLine = shape as LaneLine;
                (clone as LaneLine).color = laneLine.color;
                (clone as LaneLine).laneNumber = laneLine.laneNumber;
                (clone as LaneLine).turnType = laneLine.turnType;
            }

            return clone;
        },
        [toolManager, shapeManager]
    );

    const copyShapes = useCallback(
        (shapes?: (DetectionZone | ScreenLine | LaneLine)[]) => {
            const shapesToCopy = shapes || (shapeManager.selectedShapes.filter((s) => s.$type === DetectionZone.$type || s.$type === ScreenLine.$type || s.$type === LaneLine.$type) as (DetectionZone | ScreenLine | LaneLine)[]);

            const clonedShapes = shapesToCopy.map(cloneShape);
            setCopiedShapes(clonedShapes);
        },
        [shapeManager.selectedShapes, cloneShape]
    );

    const pasteShapes = useCallback(
        (shapes?: (DetectionZone | ScreenLine | LaneLine)[], targetPosition?: Vector2) => {
            const shapesToPaste = shapes || copiedShapes;
            if (shapesToPaste.length === 0) return;

            let freshClones: (DetectionZone | ScreenLine | LaneLine)[];

            if (shapes) {
                freshClones = shapes.map((shape) => {
                    const clone = cloneShape(shape);
                    clone.points = shape.points?.map((point) => new Vector2({ x: point.x, y: point.y })) ?? [];
                    return clone;
                });
            } else {
                const originalCenter = calculateShapesCenter(copiedShapes);

                freshClones = copiedShapes.map((shape) => {
                    const clone = cloneShape(shape);

                    // If we have a target position, offset all points relative to that position
                    if (targetPosition && originalCenter) {
                        const offsetX = targetPosition.x - originalCenter.x;
                        const offsetY = targetPosition.y - originalCenter.y;

                        clone.points = clone.points?.map((point) => new Vector2({ x: point.x + offsetX, y: point.y + offsetY })) ?? [];
                    }

                    return clone;
                });
            }

            // add to shape manager
            freshClones.forEach((shape) => shapeManager.addShape(shape));

            // Select the pasted shapes
            shapeManager.selectShapes(freshClones as MapShapeTypes[]);

            // Clear copied shapes after pasting
            setCopiedShapes([]);
        },
        [copiedShapes, cloneShape, shapeManager]
    );

    const clearCopiedShapes = useCallback(() => {
        setCopiedShapes([]);
    }, []);

    const canDeleteShape = useCallback(
        async (shapeId: string): Promise<boolean> => {
            // check if this is a temp ID (numeric) or a real server ID (GUID)
            const isTempId = /^\d+$/.test(shapeId);

            if (isTempId) {
                return true;
            }

            if (!facilityId) return false;

            try {
                const result = await Facilities.checkIfCanDeleteShape(facilityId, shapeId);
                return result?.canDelete ?? false;
            } catch (error) {
                console.error("Error checking if shape can be deleted:", error);
                return false;
            }
        },
        [facilityId]
    );

    return {
        copiedShapes,
        handleShapeChanged,
        copyShapes,
        pasteShapes,
        splitSpot,
        cloneShape,
        clearCopiedShapes,
        canDeleteShape,
    };
}
